import React, { createContext, useContext, useState } from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as LabelPrimitive from '@radix-ui/react-label';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Box, Flex, Text } from './legacy-layout';
import { resolveColorValue, splitStyleProps } from './style-props';
import { cn } from './utils';

const RadioGroupContext = createContext(null);

export function Input(props) {
  const { style, rest } = splitStyleProps(props);
  return (
    <input
      className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
      style={style}
      required={rest.isRequired}
      {...rest}
    />
  );
}

export function Textarea(props) {
  const { style, rest } = splitStyleProps(props);
  return (
    <textarea
      className="flex min-h-[80px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
      style={style}
      required={rest.isRequired}
      {...rest}
    />
  );
}

export function Select(props) {
  const { style, rest } = splitStyleProps(props);
  return (
    <select
      className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm outline-none"
      style={style}
      {...rest}
    />
  );
}

export function FormControl(props) {
  return <Box {...props} />;
}

export function FormLabel(props) {
  return <LabelPrimitive.Root className="text-sm font-medium leading-none" {...props} />;
}

export function ButtonGroup(props) {
  return <Flex {...props} />;
}

export function IconButton({ icon, _hover, ...props }) {
  const { style, rest } = splitStyleProps(props);
  const hoverBg = _hover && typeof _hover === 'object' ? _hover.bg || _hover.backgroundColor : undefined;
  return (
    <button
      type="button"
      className="inline-flex items-center justify-center rounded-md border border-input bg-white p-2 text-sm hover:opacity-90"
      style={{
        ...style,
        ...(hoverBg ? { backgroundColor: resolveColorValue(hoverBg) } : {})
      }}
      {...rest}
    >
      {icon}
    </button>
  );
}

export function Button({ children, isLoading, isDisabled, loadingText, leftIcon, rightIcon, size, ...props }) {
  const { style, rest } = splitStyleProps(props);
  const sizeStyle =
    size === 'sm'
      ? { padding: '0.25rem 0.75rem', fontSize: '0.875rem' }
      : size === 'lg'
        ? { padding: '0.75rem 1.5rem', fontSize: '1rem' }
        : {};

  return (
    <button
      type={rest.type || 'button'}
      disabled={isDisabled || isLoading}
      className="inline-flex items-center justify-center rounded-md border px-4 py-2 text-sm font-medium disabled:opacity-50"
      style={{ ...sizeStyle, ...style }}
      {...rest}
    >
      {leftIcon}
      {isLoading ? loadingText || children : children}
      {rightIcon}
    </button>
  );
}

export function RadioGroup({ children, value, defaultValue, onChange, ...props }) {
  const [internalValue, setInternalValue] = useState(defaultValue);

  const handleChange = (nextValue) => {
    if (value === undefined) {
      setInternalValue(nextValue);
    }
    onChange?.({ target: { value: nextValue } });
  };

  const currentValue = value ?? internalValue;

  return (
    <RadioGroupContext.Provider value={{ onChange: handleChange }}>
      <RadioGroupPrimitive.Root
        value={currentValue}
        onValueChange={handleChange}
        {...props}
      >
        {children}
      </RadioGroupPrimitive.Root>
    </RadioGroupContext.Provider>
  );
}

export function Radio({ value, children, onChange, ...props }) {
  const ctx = useContext(RadioGroupContext);
  const { style, rest } = splitStyleProps(props);

  return (
    <label
      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', ...style }}
      {...rest}
    >
      <RadioGroupPrimitive.Item
        value={value}
        className="aspect-square h-4 w-4 rounded-full border border-primary text-primary outline-none"
        onClick={() => {
          ctx?.onChange?.(value);
          onChange?.({ target: { value } });
        }}
      >
        <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
          <span className="h-2 w-2 rounded-full bg-primary" />
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>
      {children}
    </label>
  );
}

export function Checkbox(props) {
  return (
    <CheckboxPrimitive.Root
      className="peer h-4 w-4 shrink-0 rounded-sm border border-primary"
      {...props}
    >
      <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
        ✓
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export function Progress({ value = 0, ...props }) {
  return (
    <ProgressPrimitive.Root className="relative h-2 w-full overflow-hidden rounded-full bg-secondary" {...props}>
      <ProgressPrimitive.Indicator
        className="h-full bg-primary transition-all"
        style={{ width: `${value}%` }}
      />
    </ProgressPrimitive.Root>
  );
}

export function Tooltip({ label, children }) {
  return (
    <TooltipPrimitive.Provider>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content className="rounded-md bg-black px-2 py-1 text-xs text-white">
            {label}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

const TAG_COLORS = {
  green: 'bg-green-100 text-green-800',
  red: 'bg-red-100 text-red-800',
  blue: 'bg-blue-100 text-blue-800',
  orange: 'bg-orange-100 text-orange-800',
  purple: 'bg-purple-100 text-purple-800',
  gray: 'bg-gray-100 text-gray-800'
};

export function Tag({ colorScheme = 'gray', children, ...props }) {
  const { style, rest } = splitStyleProps(props);
  return (
    <span
      className={cn('inline-flex items-center rounded-md px-2 py-1 text-xs font-medium', TAG_COLORS[colorScheme] || TAG_COLORS.gray)}
      style={style}
      {...rest}
    >
      {children}
    </span>
  );
}

export function Badge(props) {
  return <Tag {...props} />;
}

export function Alert({ status = 'info', children, ...props }) {
  const colors = {
    info: 'bg-blue-50 border-blue-200 text-blue-900',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-900',
    error: 'bg-red-50 border-red-200 text-red-900',
    success: 'bg-green-50 border-green-200 text-green-900'
  };
  return (
    <Box border="1px solid" borderRadius="md" p={4} className={colors[status]} {...props}>
      {children}
    </Box>
  );
}

export function AlertIcon(props) {
  return <Box as="span" mr={3} {...props} />;
}

export function AlertTitle(props) {
  return <Text as="b" {...props} />;
}

export function AlertDescription(props) {
  return <Text {...props} />;
}

export function Avatar({ name, size = 'md', backgroundColor = '#8954BA', color = '#fff', src, ...props }) {
  const initials = name
    ?.split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const sizes = { sm: 32, md: 40, lg: 48 };
  const dimension = sizes[size] || sizes.md;

  if (src) {
    return (
      <Box
        as="img"
        src={src}
        alt={name}
        borderRadius="full"
        w={`${dimension}px`}
        h={`${dimension}px`}
        objectFit="cover"
        {...props}
      />
    );
  }

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      borderRadius="full"
      backgroundColor={backgroundColor}
      color={color}
      w={`${dimension}px`}
      h={`${dimension}px`}
      fontSize={size === 'sm' ? '12px' : '14px'}
      fontWeight="600"
      {...props}
    >
      {initials}
    </Box>
  );
}

export function Card(props) {
  return <Box border="1px solid #e2e8f0" borderRadius="lg" overflow="hidden" bg="white" {...props} />;
}

export function CardHeader(props) {
  return <Box p={4} borderBottom="1px solid #e2e8f0" {...props} />;
}

export function CardBody(props) {
  return <Box p={4} {...props} />;
}

export function Table(props) {
  return <Box as="table" width="100%" {...props} />;
}

export function Thead(props) {
  return <Box as="thead" {...props} />;
}

export function Tbody(props) {
  return <Box as="tbody" {...props} />;
}

export function Tr(props) {
  return <Box as="tr" {...props} />;
}

export function Th(props) {
  return <Box as="th" textAlign="left" p={2} borderBottom="1px solid #e2e8f0" {...props} />;
}

export function Td(props) {
  return <Box as="td" p={2} borderBottom="1px solid #e2e8f0" {...props} />;
}

export function InputGroup(props) {
  return <Box position="relative" display="flex" alignItems="center" {...props} />;
}

export function InputRightElement(props) {
  return <Box position="absolute" right={3} top="50%" transform="translateY(-50%)" {...props} />;
}

export function InputLeftElement(props) {
  return <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" {...props} />;
}
